<script setup lang="ts">
import LoadingButton from "@/components/LoadingButton.vue"
import type { Account } from "@/model/app/account"
import type { OrderItem } from "@/model/order/order-item"
import type { OrderProcess } from "@/model/order/order-process"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, useHttpClient, useHttpGet } from "@/vf"
import { computed, ref } from "vue"

const props = withDefaults(
    defineProps<{
        shopOrderId: string
        shopConfigurationId: string
        /** Allows the buyer to change the assignments of items that are still within their order deadline. */
        assignmentsEditable?: boolean
    }>(),
    {
        assignmentsEditable: false,
    },
)

const http = useHttpClient()

const { data: orderProcess, refresh } = await useHttpGet<OrderProcess>(
    `/order/shop/${props.shopConfigurationId}/${props.shopOrderId}/order-process/`,
)

const taxCalculation = computed(() => {
    return (
        orderProcess.value?.taxCalculation || {
            hasTax: false,
            taxByRate: {},
            totalTaxAmount: 0,
            netTotal: 0,
        }
    )
})

const assignableMembers = ref<Account[]>([])
const assignableMembersLoaded = ref(false)

async function loadAssignableMembers() {
    if (assignableMembersLoaded.value) {
        return
    }

    const organizationId = orderProcess.value?.organization?.id
    if (organizationId) {
        const response = await http.get<{ members: Account[] }>(
            `/order/shop/${props.shopConfigurationId}/assignable-members`,
            { organization: organizationId },
        )
        assignableMembers.value = response.data.members ?? []
    }

    assignableMembersLoaded.value = true
}

const editingItemId = ref<string | null>(null)
const edits = ref<Array<{ position: number; accountId: string | null; assignedName: string }>>([])
const saving = ref(false)

function canEditAssignments(item: OrderItem): boolean {
    return props.assignmentsEditable && !!item.isAssignmentEditable
}

async function startEdit(item: OrderItem) {
    if (item.purchasableItem?.isNominalRegistrationRequired) {
        await loadAssignableMembers()
    }

    edits.value = Array.from({ length: item.amount ?? 0 }, (_, index) => {
        const position = index + 1
        const existing = (item.assignments ?? []).find(assignment => assignment.position === position)
        return {
            position,
            accountId: existing?.account?.id ?? null,
            assignedName: existing?.assignedName ?? "",
        }
    })
    editingItemId.value = item.id!
}

function cancelEdit() {
    editingItemId.value = null
    edits.value = []
}

async function save(item: OrderItem) {
    const nominal = item.purchasableItem?.isNominalRegistrationRequired

    saving.value = true
    try {
        await http.put(`/order/shop/${props.shopConfigurationId}/${props.shopOrderId}/assignments`, [
            {
                id: item.id,
                assignments: edits.value.map(edit => ({
                    position: edit.position,
                    accountId: nominal ? edit.accountId ?? null : null,
                    assignedName: nominal ? null : edit.assignedName,
                })),
            },
        ])
        await refresh()
        cancelEdit()
    } finally {
        saving.value = false
    }
}

const trans = defineTranslations({
    sku: {
        de: "SKU",
        en: "SKU",
    },
    product: {
        de: "Produkt",
        en: "Product",
    },
    unit_price: {
        de: "Einzelpreis",
        en: "Unit price",
    },
    quantity: {
        de: "Stückzahl",
        en: "Quantity",
    },
    total: {
        de: "Gesamtpreis",
        en: "Total",
    },
    net_amount: {
        de: "Nettobetrag",
        en: "Net amount",
    },
    vat_amount: {
        de: "zzgl. {rate}% Umsatzsteuer",
        en: "plus {rate}% VAT",
    },
    total_amount: {
        de: "Gesamtsumme",
        en: "Total amount",
    },
    select_placeholder: {
        de: "– Person auswählen –",
        en: "– Select person –",
    },
})
</script>

<template>
    <div class="table-responsive">
        <table class="table table-borderless table-striped mt-4">
            <thead>
                <tr>
                    <th>{{ trans("sku") }}</th>
                    <th>{{ trans("product") }}</th>
                    <th class="text-right">{{ trans("unit_price") }}</th>
                    <th>{{ trans("quantity") }}</th>
                    <th class="text-right">{{ trans("total") }}</th>
                </tr>
            </thead>
            <tbody>
                <template v-for="item of orderProcess?.items" :key="item.id">
                    <tr>
                        <td>{{ item.purchasableItem?.sku }}</td>
                        <td>
                            {{ item.title }}
                            <template v-if="editingItemId === item.id">
                                <div v-for="edit in edits" :key="edit.position" class="d-flex align-items-center mb-1">
                                    <div class="mr-2">
                                        <small class="text-muted">{{ edit.position }}.</small>
                                    </div>
                                    <div class="">
                                        <select
                                            v-if="item.purchasableItem?.isNominalRegistrationRequired"
                                            v-model="edit.accountId"
                                            class="form-control form-control-sm"
                                        >
                                            <option :value="null">
                                                {{ trans("select_placeholder") }}
                                            </option>
                                            <option
                                                v-for="account in assignableMembers"
                                                :key="account.id"
                                                :value="account.id"
                                            >
                                                {{ account.name }}
                                            </option>
                                        </select>
                                        <input
                                            v-else
                                            v-model="edit.assignedName"
                                            type="text"
                                            class="form-control form-control-sm"
                                        />
                                    </div>
                                </div>
                                <div class="d-flex gap-2 mt-2">
                                    <button type="button" class="btn btn-sm btn-secondary" @click="cancelEdit">
                                        {{ $t("crud:button.abort") }}
                                    </button>
                                    <LoadingButton
                                        type="button"
                                        class="btn btn-sm btn-primary"
                                        :loading="saving"
                                        @click="save(item)"
                                    >
                                        {{ $t("crud:button.save") }}
                                    </LoadingButton>
                                </div>
                            </template>
                            <template v-else>
                                <div class="row align-items-end">
                                    <div class="col">
                                        <div v-for="assignment in item.assignments" :key="assignment.id">
                                            <small class="text-muted">
                                                {{ assignment.position }}.
                                                {{
                                                    assignment.account
                                                        ? assignment.account.name
                                                        : assignment.assignedName
                                                }}
                                            </small>
                                        </div>
                                    </div>
                                    <div class="col">
                                        <button
                                            v-if="canEditAssignments(item)"
                                            type="button"
                                            class="btn btn-primary"
                                            @click="startEdit(item)"
                                        >
                                            <i class="fa fa-fw fa-edit"></i>
                                        </button>
                                    </div>
                                </div>
                            </template>
                        </td>
                        <td class="text-right">
                            {{ $currency(item.priceInCents, 100, { currencyDisplay: "name" }) }}
                        </td>
                        <td>{{ item.amount }}</td>
                        <td class="text-right">
                            {{
                                $currency(item.amount! * item.priceInCents!, 100, {
                                    currencyDisplay: "name",
                                })
                            }}
                        </td>
                    </tr>
                </template>
            </tbody>
            <tfoot>
                <template v-if="taxCalculation.hasTax">
                    <tr class="border-top">
                        <th colspan="4" class="text-right">{{ trans("net_amount") }}</th>
                        <td class="text-right">
                            {{
                                $currency(taxCalculation.netTotal, 100, {
                                    currencyDisplay: "name",
                                })
                            }}
                        </td>
                    </tr>
                    <tr v-for="(taxData, vatKey) in taxCalculation.taxByRate" :key="vatKey">
                        <th colspan="4" class="text-right">
                            {{ trans("vat_amount", { rate: taxData.rate }) }}
                        </th>
                        <td class="text-right">
                            {{ $currency(taxData.amount, 100, { currencyDisplay: "name" }) }}
                        </td>
                    </tr>
                    <tr class="border-top">
                        <th colspan="4" class="text-right">
                            <strong>{{ trans("total_amount") }}</strong>
                        </th>
                        <td class="text-right">
                            <strong>
                                {{
                                    $currency(taxCalculation.netTotal! + taxCalculation.totalTaxAmount!, 100, {
                                        currencyDisplay: "name",
                                    })
                                }}
                            </strong>
                        </td>
                    </tr>
                </template>
                <template v-else>
                    <tr class="border-top">
                        <th colspan="4" class="text-right">{{ trans("total_amount") }}</th>
                        <td class="text-right">
                            {{
                                $currency(orderProcess?.totalInCents, 100, {
                                    currencyDisplay: "name",
                                })
                            }}
                        </td>
                    </tr>
                </template>
            </tfoot>
        </table>
    </div>
</template>
